import BpkButton, {
  BUTTON_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-button';
import BpkCard from '@skyscanner-internal/backpack-web/bpk-component-card';
import BpkSmallAircraftIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/aircraft';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';

import './ItineraryCard.scss';
import { useTranslation } from '../../helpers/hooks/useTranslation';

type FlightLeg = {
  departureTime: string;
  arrivalTime: string;
  departureStation: string;
  arrivalStation: string;
  duration: string;
  stopCount: number;
  stopsLabel: string;
};

type ItineraryCardProps = {
  airlineName: string;
  airlineLogoUrl?: string;
  outbound: FlightLeg;
  inbound?: FlightLeg;
  ariaLabel: string;
  onShowDetails?: (id: string | undefined) => void;
  onCardClick?: () => void;
  id: string | undefined;
  showButton?: boolean;
};

const AirlineHeader = ({
  airlineLogoUrl,
  airlineName,
}: {
  airlineLogoUrl?: string;
  airlineName: string;
}) => (
  <div className="itinerary-card__airline">
    {airlineLogoUrl ? (
      <img
        className="itinerary-card__airline-logo"
        src={airlineLogoUrl}
        alt=""
        aria-hidden="true"
      />
    ) : (
      <BpkSmallAircraftIcon className="itinerary-card__airline-logo-fallback" />
    )}
    <BpkText
      textStyle={TEXT_STYLES.caption}
      className="itinerary-card__airline-name"
    >
      {airlineName}
    </BpkText>
  </div>
);

const StopIndicator = ({
  duration,
  stopCount,
  stopsLabel,
}: {
  duration: string;
  stopCount: number;
  stopsLabel: string;
}) => (
  <div className="itinerary-card__stops-col">
    <BpkText textStyle={TEXT_STYLES.caption}>{duration}</BpkText>
    <div className="itinerary-card__stop-line">
      {Array.from({ length: stopCount }, (_, i) => (
        <span key={i} className="itinerary-card__stop-dot" />
      ))}
      <BpkSmallAircraftIcon
        className="itinerary-card__plane-icon"
        aria-hidden="true"
      />
    </div>
    <BpkText
      textStyle={TEXT_STYLES.caption}
      className={
        stopCount > 0
          ? 'itinerary-card__stops-label--connecting'
          : 'itinerary-card__stops-label--direct'
      }
    >
      {stopsLabel}
    </BpkText>
  </div>
);

const LegDetails = ({ leg }: { leg: FlightLeg }) => (
  <div className="itinerary-card__leg">
    <div className="itinerary-card__route-depart">
      <BpkText textStyle={TEXT_STYLES.label1} tagName="p">
        {leg.departureTime}
      </BpkText>
      <BpkText tagName="p" className="itinerary-card__station-code">
        {leg.departureStation}
      </BpkText>
    </div>

    <StopIndicator
      duration={leg.duration}
      stopCount={leg.stopCount}
      stopsLabel={leg.stopsLabel}
    />

    <div className="itinerary-card__route-arrive">
      <BpkText textStyle={TEXT_STYLES.label1} tagName="p">
        {leg.arrivalTime}
      </BpkText>
      <BpkText tagName="p" className="itinerary-card__station-code">
        {leg.arrivalStation}
      </BpkText>
    </div>
  </div>
);

export const ItineraryCard = ({
  airlineLogoUrl,
  airlineName,
  ariaLabel,
  id,
  inbound,
  onCardClick,
  onShowDetails,
  outbound,
  showButton,
}: ItineraryCardProps) => {
  const translate = useTranslation();

  return (
    <BpkCard
      className="itinerary-card"
      aria-label={ariaLabel}
      padded
      atomic={false}
      onClick={onCardClick}
    >
      <AirlineHeader
        airlineLogoUrl={airlineLogoUrl}
        airlineName={airlineName}
      />
      <LegDetails leg={outbound} />
      {inbound && (
        <>
          <hr className="itinerary-card__separator" />
          <LegDetails leg={inbound} />
        </>
      )}
      {showButton && (
        <BpkButton
          type={BUTTON_TYPES.secondary}
          fullWidth
          className="itinerary-card__show-details"
          onClick={() => onShowDetails?.(id)}
        >
          {translate('mv_cheapestDates_dealHunter_show_flight_details_cta')}
        </BpkButton>
      )}
    </BpkCard>
  );
};

export const ItineraryCardLoading = () => (
  <BpkCard className="itinerary-card itinerary-card--loading" padded>
    <span
      className="itinerary-card__spinner"
      aria-label="Loading flight details"
    />
  </BpkCard>
);

export type { FlightLeg, ItineraryCardProps };
